import {createBrowserRouter, createRoutesFromElements, Navigate, Route} from 'react-router-dom'
import {PrivateRoute} from '@route-guards/private-route/PrivateRoute.tsx'
import {PublicRoute} from '@route-guards/public-route/PublicRoute.tsx'
import {PublicLayout} from '@layouts/public-layout/PublicLayout.tsx'
import {PrivateLayout} from '@layouts/private-layout/PrivateLayout.tsx'
import {routes} from '@utilities/constants/routes.tsx'
import {TreatmentRequestsTableLayout} from '@/layouts/treatment-requests-table-layout/TreatmentRequestsTableLayout'
import {PatientDetailsLayout} from '@/layouts/patient-details-layout/PatientDetailsLayout'
import {RouterErrorBoundary} from '@/route-guards/router-error-boundary/RouterErrorBoundary'
import {ProjectDetailsLayout} from '@/layouts/project-details-layout/ProjectDetailsLayout'
import {ProjectDetailsLogsRoute} from '@/route-guards/project-details-logs-route/ProjectDetailsLogsRoute'
import {AccessesLayout} from '@/layouts/accesses-layout/AccessesLayout'
import {DashboardLayout} from '@/layouts/dashboard-layout/DashboardLayout'
import {ProjectsLayout} from '@/layouts/projects-layout/ProjectsLayout'

export const router = createBrowserRouter(
    createRoutesFromElements(
        <Route errorElement={<RouterErrorBoundary />}>
            <Route {...routes.EMAIL_VERIFICATION_ROUTE} />
            <Route {...routes.REQUEST_ACCOUNT_DELETION} />
            <Route {...routes.PRIVACY_POLICY} />
            <Route element={<PrivateRoute />}>
                <Route element={<PrivateLayout />}>
                    <Route {...routes.HOME} />
                    <Route {...routes.DASHBOARD}>
                        <Route index element={<Navigate to={routes.DASHBOARD_OPERATIONAL.path} replace />} />
                        <Route element={<DashboardLayout />}>
                            <Route {...routes.DASHBOARD_OPERATIONAL} />
                            <Route {...routes.DASHBOARD_CONSULTANT} />
                            <Route {...routes.DASHBOARD_ECONOMIC} />
                        </Route>
                    </Route>
                    <Route {...routes.AGENDA_ROUTE}>
                        <Route index element={<Navigate to="users" replace />} />
                        <Route {...routes.AGENDA} />
                    </Route>
                    <Route {...routes.TREATMENT_REQUESTS_ROUTE}>
                        <Route {...routes.CREATE_TREATMENT_REQUEST} />
                        <Route {...routes.TREATMENT_REQUEST_DETAILS_ROUTE}>
                            <Route {...routes.TREATMENT_REQUEST_DETAILS} />
                        </Route>
                        <Route element={<TreatmentRequestsTableLayout />}>
                            <Route {...routes.TREATMENT_REQUESTS_ALL} />
                            <Route {...routes.TREATMENT_REQUESTS_IN_REVIEW} />
                            <Route {...routes.TREATMENT_REQUESTS_WAITING_LIST} />
                            <Route {...routes.TREATMENT_REQUESTS_IN_PROGRESS} />
                            <Route {...routes.TREATMENT_REQUESTS_ACCEPTED} />
                            <Route {...routes.TREATMENT_REQUESTS_REJECTED} />
                        </Route>
                    </Route>
                    <Route {...routes.PATIENTS_ROUTE}>
                        <Route {...routes.PATIENTS} />
                        <Route {...routes.CREATE_PATIENT} />
                        <Route {...routes.PATIENT_DETAILS_ROUTE}>
                            <Route index element={<Navigate to={routes.PATIENT_DETAILS_INFORMATION.path} replace />} />
                            <Route {...routes.CREATE_MEDICAL_REPORT} />
                            <Route {...routes.EDIT_MEDICAL_REPORT} />
                            <Route element={<PatientDetailsLayout />}>
                                <Route {...routes.PATIENT_DETAILS_INFORMATION} />
                                <Route {...routes.PATIENT_DETAILS_MEDICAL_RECORDS} />
                                <Route {...routes.PATIENT_DETAILS_EVENTS} />
                            </Route>
                        </Route>
                    </Route>
                    <Route {...routes.PROJECTS_ROUTE}>
                        <Route element={<ProjectsLayout />}>
                            <Route {...routes.PROJECTS} />
                            <Route {...routes.SIAR_EXPORT_HISTORY} />
                        </Route>
                        <Route {...routes.CREATE_PROJECT} />
                        <Route {...routes.PROJECT_DETAILS_ROUTE}>
                            <Route index element={<Navigate to={routes.PROJECT_DETAILS_PROJECT_DATA.path} replace />} />
                            <Route element={<ProjectDetailsLayout />}>
                                <Route {...routes.PROJECT_DETAILS_PROJECT_DATA} />
                                <Route {...routes.PROJECT_DETAILS_OTHER_DATA} />
                                <Route {...routes.PROJECT_DETAILS_DIAGNOSES_FACILITIES} />
                                <Route {...routes.PROJECT_DETAILS_DISABILITY_PROFILE} />
                                <Route {...routes.PROJECT_DETAILS_SCALES_TESTS} />
                                <Route {...routes.PROJECT_DETAILS_NOTES} />
                                <Route {...routes.PROJECT_DETAILS_PLANNED_ACCESSES} />
                                <Route element={<ProjectDetailsLogsRoute />}>
                                    <Route {...routes.PROJECT_DETAILS_LOGS} />
                                </Route>
                            </Route>
                            <Route {...routes.CREATE_PLANNED_ACCESSES} />
                            <Route {...routes.EDIT_PLANNED_ACCESSES} />
                            <Route {...routes.PROJECT_RENEW} />
                            <Route {...routes.PROJECT_TRANSFORM} />
                            <Route {...routes.PROJECT_EARLY_CLOSING} />
                            <Route {...routes.PROJECT_EXTENDS_CYCLE} />
                            <Route {...routes.PROJECT_REOPENING} />
                        </Route>
                    </Route>
                    <Route {...routes.ACCESSES_ROUTE}>
                        <Route index element={<Navigate to={routes.ACCESSES_TABLE.path} replace />} />
                        <Route element={<AccessesLayout />}>
                            <Route {...routes.ACCESSES_TABLE} />
                            <Route {...routes.ACCESSES_VALIDATOR} />
                            <Route {...routes.ACTIVITY_REPORT_HISTORY} />
                        </Route>
                    </Route>
                    <Route {...routes.USERS_ROUTE}>
                        <Route {...routes.USERS} />
                        <Route {...routes.CREATE_USER} />
                        <Route {...routes.USER_DETAILS}>
                            <Route index element={<Navigate to={routes.USER_DETAILS_INFORMATION.path} replace />} />
                            <Route {...routes.USER_DETAILS_ACCESS_PROFILE} />
                            <Route {...routes.USER_DETAILS_INFORMATION} />
                            <Route {...routes.USER_DETAILS_AVAILABILITY} />
                            <Route {...routes.USER_DETAILS_EVENTS} />
                        </Route>
                    </Route>
                    <Route {...routes.BUDGET_ROUTE}>
                        <Route index element={<Navigate to={routes.BUDGET_PROJECTS.path} replace />} />
                        <Route {...routes.BUDGET_PROJECTS} />
                        <Route {...routes.BUDGET_EXTERNAL_COSTS} />
                        <Route {...routes.BUDGET_ACCESS_RATES} />
                    </Route>
                    <Route {...routes.USER_GROUPS_ROUTE}>
                        <Route {...routes.USER_GROUPS} />
                    </Route>
                    <Route {...routes.COMPANY_INFO_ROUTE}>
                        <Route {...routes.COMPANY_INFO} />
                    </Route>
                    <Route {...routes.REFERRING_SPECIALIST} />
                    <Route {...routes.MAILER_ROUTE}>
                        <Route {...routes.MAILER} />
                    </Route>
                </Route>
            </Route>
            <Route element={<PublicRoute />}>
                <Route element={<PublicLayout />}>
                    <Route {...routes.LOGIN} />
                    <Route {...routes.FORGOT_PASSWORD} />
                    <Route {...routes.RESET_PASSWORD} />
                    <Route {...routes.SET_PASSWORD} />
                </Route>
            </Route>
        </Route>
    )
)
